// frontend/src/components/reporting/activity/UserActivityListPage.js — RPT-001
// User Activity List: entry point into the RPT-001/002/003 drilldown.
// SuperAdmin/Admin/Coach only — see documentation/reporting-dashboard/report-catalog-filled-instances.md.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CaretRight } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useRoles } from '../../../hooks/useRoles';
import { useApiCall } from '../../../hooks/useApiCall';
import apiService from '../../../services/apiService';
import { exportToCSV, formatDateForCSV } from '../../../utils/csvExport';
import '../../../styles/activityReporting.css';

const UserActivityListPage = () => {
  const { user } = useAuth();
  const { userProfile, isSuperAdmin, isAdmin, isCoach } = useRoles();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedCohortIds, setSelectedCohortIds] = useState([]);
  const { loading, error, makeRequest } = useApiCall();

  const canAccess = isSuperAdmin() || isAdmin() || isCoach();
  const targetTenantId = searchParams.get('targetTenantId') || userProfile?.tenant?.id || null;

  // SuperAdmin: needs a tenant picker, since RPT-001 requires an explicit
  // targetTenantId for every request (never "all tenants" — privacy by design).
  // isSuperAdmin is intentionally left out of the deps array — useRoles()
  // returns a brand-new function reference on every render, so including it
  // here would re-fire this effect (and re-fetch /api/v1/tenants) on every
  // single re-render, forever. userProfile already covers "roles are known."
  const isSuperAdminUser = !!userProfile && isSuperAdmin();
  useEffect(() => {
    if (!isSuperAdminUser) return;
    (async () => {
      try {
        const data = await apiService.get('/api/v1/tenants', user);
        if (data.success) setTenants(data.data?.tenants || data.data || []);
      } catch (err) {
        console.error('Failed to load tenants:', err);
      }
    })();
  }, [isSuperAdminUser, user]);

  const handleSelectTenant = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('targetTenantId', String(id));
    setSearchParams(next);
  };

  useEffect(() => {
    if (!targetTenantId) return;
    setSelectedCohortIds([]);
    (async () => {
      try {
        const data = await apiService.get(`/api/v1/tenants/${targetTenantId}/cohorts`, user);
        if (data.success) setCohorts(data.data?.cohorts || []);
      } catch (err) {
        console.error('Failed to load cohorts:', err);
      }
    })();
  }, [targetTenantId, user]);

  const loadUsers = useCallback(async () => {
    if (!targetTenantId) return;
    try {
      const params = new URLSearchParams({ targetTenantId: String(targetTenantId) });
      if (selectedCohortIds.length > 0) params.set('cohort_ids', selectedCohortIds.join(','));

      const data = await makeRequest(
        () => apiService.get(`/api/v1/reporting/user-activity?${params.toString()}`, user),
        'Loading user activity…'
      );
      if (data.success) setUsers(data.data.users || []);
    } catch (err) {
      console.error('Failed to load user activity list:', err);
    }
  }, [targetTenantId, selectedCohortIds, user, makeRequest]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCohortToggle = (id) => {
    setSelectedCohortIds((prev) => (
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    ));
  };

  const handleClearCohorts = () => setSelectedCohortIds([]);

  const handleRowClick = (userId) => {
    navigate(`/app/reports/activity/users/${userId}?targetTenantId=${targetTenantId}`);
  };

  const handleDownloadCSV = () => {
    if (!users.length) return;
    const columns = [
      { key: 'username', label: 'Username' },
      { key: 'lastActivityAt', label: 'Last Activity', format: formatDateForCSV },
      { key: 'conversationCount', label: 'Conversations' },
      { key: 'totalMessageCount', label: 'Messages' },
      { key: 'debriefReportCount', label: 'Debriefs' },
    ];
    const result = exportToCSV(users, columns, 'user-activity-list');
    if (!result.success) alert(`Failed to export CSV: ${result.error}`);
  };

  if (!canAccess) {
    return (
      <div className="activity-report-page">
        <div className="arp-state">You do not have permission to view this report.</div>
      </div>
    );
  }

  return (
    <div className="activity-report-page">
      <div className="arp-breadcrumb">
        <Link to="/app/reports" className="arp-breadcrumb-link">Reporting Dashboard</Link>
        <CaretRight size={11} weight="bold" />
        <span>User Activity Report</span>
      </div>

      <div className="arp-header">
        <div>
          <h1 className="arp-title">User Activity</h1>
          <p className="arp-subtitle">Everyone you&apos;re authorized to review, with summary activity.</p>
        </div>
        <div className="arp-actions">
          <button className="arp-btn arp-btn-secondary" onClick={handleDownloadCSV} disabled={!users.length}>
            Export CSV
          </button>
        </div>
      </div>

      {isSuperAdmin() && (
        <div className="arp-filters">
          <div className="arp-filter-group">
            <label className="arp-filter-label" htmlFor="arp-tenant-select">Tenant</label>
            <select
              id="arp-tenant-select"
              className="arp-filter-select"
              value={targetTenantId || ''}
              onChange={(e) => handleSelectTenant(e.target.value)}
            >
              <option value="" disabled>Select a tenant…</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {targetTenantId && (
        <div className="arp-filters">
          <div className="arp-filter-group">
            <div className="arp-filter-label-row">
              <span className="arp-filter-label">Cohorts</span>
              {selectedCohortIds.length > 0 && (
                <button type="button" className="arp-filter-clear" onClick={handleClearCohorts}>
                  Clear
                </button>
              )}
            </div>
            <div className="arp-filter-checkbox-list" role="group" aria-label="Filter by cohort">
              {cohorts.map((c) => (
                <label key={c.id} className="arp-filter-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCohortIds.includes(String(c.id))}
                    onChange={() => handleCohortToggle(String(c.id))}
                  />
                  {c.name}
                </label>
              ))}
            </div>
            <span className="arp-filter-hint">Leave empty to see everyone you&apos;re authorized to review.</span>
          </div>
        </div>
      )}

      {error && <div className="arp-error-state">{error}</div>}

      {!targetTenantId ? (
        <div className="arp-state">Select a tenant to view its activity.</div>
      ) : loading ? (
        <div className="arp-state">Loading…</div>
      ) : users.length === 0 ? (
        <div className="arp-state">No users found.</div>
      ) : (
        <div className="arp-table-wrapper">
          <table className="arp-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Last Activity</th>
                <th>Conversations</th>
                <th>Messages</th>
                <th>Debriefs</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.userId}>
                  <td>
                    <button className="arp-row-link" onClick={() => handleRowClick(u.userId)}>
                      {u.displayName || u.username}
                    </button>
                  </td>
                  <td className="arp-cell-muted">
                    {u.lastActivityAt ? new Date(u.lastActivityAt).toLocaleString() : 'Never'}
                  </td>
                  <td>{u.conversationCount}</td>
                  <td>{u.totalMessageCount}</td>
                  <td>{u.debriefReportCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserActivityListPage;
